import { BASE_URL } from '../../config';

/**
 * Starts a background process to periodically check the JWT token validity.
 * @param {object} params - The params object.
 * @param {string} params.wallet_address - The user's wallet address.
 * @param {string} params.jwt - The user's JWT token.
 * @param {function} params.onTokenInvalid - Callback to call if the token is invalid **or fetch fails**.
 * @param {number} [params.intervalMs=900000] - (Optional) How often to check (default 15 min).
 * @param {string} [params.apiUrl] - (Optional) The API endpoint.
 * @returns {function} A cleanup function to stop the checker.
 */
export function startTokenChecker({
    wallet_address,
    jwt,
    onTokenInvalid,
    intervalMs = 900000, // 15 minutes by default
    apiUrl
}) {
    const endpoint = apiUrl || `${BASE_URL}/check_token_age`;

    if (!wallet_address || !jwt) {
        console.warn('Token checker: Missing wallet_address or jwt.');
        return () => { };
    }

    let stopped = false;

    const check = async () => {
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address: wallet_address, jwt }),
            });
            if (!res.ok) {
                // Log out on *any* error (including 429)
                if (!stopped) {
                    onTokenInvalid('API returned error status: ' + res.status);
                }
                return; // Ensure we never try to process the body
            }
            const data = await res.json();
            if (!data.valid) {
                if (!stopped) onTokenInvalid(data.reason || 'Token invalid');
            }
        } catch (err) {
            console.error('[TokenChecker] Network/API Error:', err);
            if (!stopped) onTokenInvalid('Network/API error or token invalid');
        }
    };

    check();
    const timer = setInterval(check, intervalMs);

    return () => {
        stopped = true;
        clearInterval(timer);
    };
}
